import { useState, useEffect, useRef } from "react";
import { Tabs, Tab } from "react-bootstrap";
import { useAuth } from "../../contexts/AuthProvider/useAuth";
import { isMobileDevice, toastError } from "../../commons/utils";
import { DashEntradas } from "./Entradas";
import { DashClassificacoes } from "./Classificações";
import { DashVendas } from "./Saidas";
import { DashTransformacoes } from "./Transformacoes";
import { DashPlantio } from "./Plantio";
import { DashFinanceiroGeral } from "./FinanceiroGeral";
import { DashRequisicoes } from "./Requisicoes";
import { DashColaboradores } from "./Colaboradores";
import FiltroCultura from "../FiltroCultura";
import { Grid } from "@mui/material";
import { Column } from "primereact/column";
import { useAutoScrollTabs } from "./useAutoScrollTabs";
import { ButtonAutoScroll, TabAutoScrollSelector } from "./components";

type LoadedTabs = { [key: string]: boolean };

export const GridCard = (props: any) => {
    return (
        <Grid item xs={isMobileDevice() ? 12 : props.md ? props.md : 3}>
            {props.children}
        </Grid>
    );
}

export const Columns = (columns: any) => {
    return columns?.map((col: any, i: number) => (
        <Column
            key={col.field}
            field={col.field}
            align={isMobileDevice() || col.dataType === 'numeric' ? 'right' : 'left'}
            header={col.header}
            body={col.body}
            sortable={col.notSortable ? false : true}
            style={{ ...col.style, fontSize: '14px', marginBottom: `${i + 1 === columns.length ? '0px' : '-14px'}` }}
        />
    ));
};
export const Dashboard = () => {
    const auth = useAuth();

    const [data, setData] = useState<any[]>([]);
    const [culturaChecked, setCulturaChecked] = useState<any>();
    const [loadedTabs, setLoadedTabs] = useState<LoadedTabs>({});
    const [activeTab, setActiveTab] = useState<string>("entradas");

    const refEntrada = useRef<any>(null);
    const refVenda = useRef<any>(null);
    const refClassificacao = useRef<any>(null);
    const refTransformacao = useRef<any>(null);
    const refPlantio = useRef<any>(null);
    const refFinanceiroGeral = useRef<any>(null);
    const refRequisicoes = useRef<any>(null);
    const refColaboradores = useRef<any>(null);

    const { autoScroll, setAutoScroll, showTabSelector, setShowTabSelector, scrollTabs, setScrollTabs, clearAllTimers } = useAutoScrollTabs({
        activeTab,
        setActiveTab,
        loadTabData,
        loadedTabs,
    });

    async function loadTabData(tab: string | null) {
        if (!tab) return;

        try {
            const getMovEntrada = async () => {
                const entradaResponse: any = await auth.get("/v1/movimentosentrada");
                const entradaProducao = entradaResponse.data.filter((e: any) => e.pltipo === 0);
                refEntrada.current?.setData(entradaProducao);
                setData(entradaResponse.data);
                return entradaResponse.data;
            };

            if (tab === "entradas") await getMovEntrada();

            if (tab === "classificacao") {
                const entradas = await getMovEntrada();
                const classificacaoResponse: any = await auth.get("/v1/movimentosclassificacao");
                const entradaProducao = entradas.filter((e: any) => e.pltipo === 0);
                refClassificacao.current?.setData(entradaProducao, classificacaoResponse.data);
            }

            if (tab === "vendas") {
                const entradas = await getMovEntrada();
                const saidaResponse: any = await auth.get("/v1/movimentossaida");
                const saldoResponse: any = await auth.get("/v1/movimentosaldoporproduto");
                if (!data.length) setData(saidaResponse.data);
                const entradaProducao = entradas.filter((e: any) => e.pltipo === 0);
                refVenda.current?.setData(entradaProducao, saidaResponse.data, saldoResponse.data);
            }

            if (tab === "transformacao") {
                const entradas = await getMovEntrada();
                const transformacaoResponse: any = await auth.get("/v1/movimentostransformacao");
                refTransformacao.current?.setData(entradas, transformacaoResponse.data);
            }

            if (tab === "plantio") {
                const plantioResponse: any = await auth.get("/v1/movimentosplantio");
                refPlantio.current?.setData(plantioResponse.data);
            }

            if (tab === "financeirogeral") {
                const contasReceber: any = await auth.get("/v1/contasrecebercrm");
                refFinanceiroGeral.current?.setData(contasReceber.data);
            }

            if (tab === "requisicoes") {
                const requisicoes: any = await auth.get("/v1/requisicaodash");
                refRequisicoes.current?.setData(requisicoes.data);
            }

            if (tab === "colaboradores") {
                const colaboradores: any = await auth.get("/v1/colaboradordash");
                refColaboradores.current?.setData(colaboradores.data);
            }

            setLoadedTabs(prev => ({ ...prev, [tab]: true }));
        } catch (error) {
            toastError(error);
        }
    }

    useEffect(() => {
        if (auth.useAppEstoques) {
            setActiveTab("entradas");
            loadTabData("entradas");
        } else if (auth.useAppRequisicoes) {
            setActiveTab("requisicoes");
            loadTabData("requisicoes");
        } else if (auth.useAppDP) {
            setActiveTab("colaboradores");
            loadTabData("colaboradores");
        }

        return clearAllTimers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ButtonAutoScroll autoScroll={autoScroll} setAutoScroll={setAutoScroll} setShowTabSelector={setShowTabSelector} />

            <TabAutoScrollSelector
                show={showTabSelector}
                onHide={() => setShowTabSelector(false)}
                onConfirm={() => {
                    if (scrollTabs.length > 0) {
                        setAutoScroll(true);
                        setShowTabSelector(false);
                    }
                }}
                scrollTabs={scrollTabs}
                setScrollTabs={setScrollTabs}
                auth={auth}
            />

            <Tabs
                activeKey={activeTab}
                onSelect={(tab) => {
                    if (!tab) return;

                    setActiveTab(tab);
                    loadTabData(tab);
                }}
                style={{ padding: isMobileDevice() ? "1rem" : ".8rem 2rem" }}
            >
                {auth.useAppEstoques &&
                    <Tab eventKey="entradas" title="Entradas" style={{ backgroundColor: 'var(--bg-color-secondary)' }}>
                        <FiltroCultura data={data} culturaChecked={culturaChecked} onChecked={setCulturaChecked} />
                        <DashEntradas ref={refEntrada} cultura={culturaChecked} onChangeCultura={setCulturaChecked} />
                    </Tab>
                }
                {auth.useAppEstoques &&
                    <Tab eventKey="classificacao" title="Classificação" style={{ backgroundColor: 'var(--bg-color-secondary)' }}>
                        <FiltroCultura data={data} culturaChecked={culturaChecked} onChecked={setCulturaChecked} />
                        <DashClassificacoes ref={refClassificacao} cultura={culturaChecked} />
                    </Tab>
                }
                {auth.useAppEstoques &&
                    <Tab eventKey="vendas" title="Vendas" style={{ backgroundColor: 'var(--bg-color-secondary)' }}>
                        <FiltroCultura data={data} culturaChecked={culturaChecked} onChecked={setCulturaChecked} />
                        <DashVendas ref={refVenda} cultura={culturaChecked} onChangeCultura={setCulturaChecked} />
                    </Tab>
                }
                {auth.useAppEstoques &&
                    <Tab eventKey="transformacao" title="Debulha" style={{ backgroundColor: 'var(--bg-color-secondary)' }}>
                        <FiltroCultura data={data} culturaChecked={culturaChecked} onChecked={setCulturaChecked} />
                        <DashTransformacoes ref={refTransformacao} cultura={culturaChecked} />
                    </Tab>
                }
                {auth.useAppEstoques &&
                    <Tab eventKey="plantio" title="Plantio" style={{ backgroundColor: 'var(--bg-color-secondary)' }}>
                        <FiltroCultura data={data} culturaChecked={culturaChecked} onChecked={setCulturaChecked} />
                        <DashPlantio ref={refPlantio} cultura={culturaChecked} />
                    </Tab>
                }
                {auth.useAppEstoques &&
                    <Tab eventKey="financeirogeral" title="Financeiro Geral" style={{ backgroundColor: 'var(--bg-color-secondary)' }}>
                        <DashFinanceiroGeral ref={refFinanceiroGeral} />
                    </Tab>
                }
                {auth.useAppRequisicoes &&
                    <Tab eventKey="requisicoes" title="Requisições" style={{ backgroundColor: 'var(--bg-color-secondary)' }}>
                        <DashRequisicoes ref={refRequisicoes} />
                    </Tab>
                }
                {auth.useAppDP &&
                    <Tab eventKey="colaboradores" title="Colaboradores" style={{ backgroundColor: 'var(--bg-color-secondary)' }}>
                        <DashColaboradores ref={refColaboradores} />
                    </Tab>
                }
            </Tabs>
        </>
    );
};
